import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Clock, Star, MessageCircle, Eye, Coins } from 'lucide-react'
import { supabase, Profile, isSupabaseConfigured } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { logger } from '../utils/logger'
import { OnlineIndicator } from './OnlineIndicator'
import { chatAvailabilityService } from '../services/chatAvailabilityService'
import { tokenService } from '../services/tokenService'
import { ratingService } from '../services/ratingService'
import { TokenPackage } from '../types'

interface ModelsGridProps {
  limit?: number
  showTitle?: boolean
  searchTerm?: string
  filterOnline?: boolean
  sortBy?: string
  nearbyCity?: string
  nearbyState?: string
  showTokenOffer?: boolean
}

// Dados demo para quando o Supabase não estiver configurado
const demoModels: Profile[] = [
  {
    id: 'demo-1',
    name: 'Ana Silva',
    email: 'ana@demo.com',
    bio: 'Modelo profissional com experiência em fotografia artística e comercial.',
    avatar_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
    profile_image_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
    cover_photo: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=800',
    photos: [
      'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
      'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400'
    ],
    is_online: true,
    last_seen: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'demo-2',
    name: 'Carla Santos',
    email: 'carla@demo.com',
    bio: 'Especialista em ensaios fotográficos e conteúdo exclusivo.',
    avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
    profile_image_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
    cover_photo: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800',
    photos: [
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400'
    ],
    is_online: false,
    last_seen: new Date(Date.now() - 3600000).toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'demo-3',
    name: 'Beatriz Costa',
    email: 'beatriz@demo.com',
    bio: 'Criadora de conteúdo digital e influenciadora.',
    avatar_url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
    profile_image_url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
    cover_photo: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=800',
    photos: [
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
      'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400',
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400'
    ],
    is_online: true,
    last_seen: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'demo-4',
    name: 'Diana Oliveira',
    email: 'diana@demo.com',
    bio: 'Modelo fitness e lifestyle, apaixonada por fotografia.',
    avatar_url: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400',
    profile_image_url: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400',
    cover_photo: 'https://images.unsplash.com/photo-1506863530036-1efeddceb993?w=800',
    photos: [
      'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400'
    ],
    is_online: false,
    last_seen: new Date(Date.now() - 7200000).toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
]

export function ModelsGrid({
  limit,
  showTitle = true,
  searchTerm,
  filterOnline = false,
  sortBy = 'newest',
  nearbyCity,
  nearbyState,
  showTokenOffer = false
}: ModelsGridProps) {
  const { user } = useAuth()
  const [rawModels, setRawModels] = useState<Profile[]>([])
  const [models, setModels] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tokenOffers, setTokenOffers] = useState<TokenPackage[]>([])
  const [highlightedOfferId, setHighlightedOfferId] = useState<string | null>(null)
  const [modelRatings, setModelRatings] = useState<Record<string, { average: number; count: number }>>({})

  const normalize = (value: string | null | undefined) => (value || '').trim().toLowerCase()
  const getDisplayName = (model: Profile) => (model.name || model.full_name || '').toString()

  const getLocalIdSet = (baseKey: string) => {
    try {
      const userKey = user?.id || 'guest'
      const raw = localStorage.getItem(`${baseKey}_${userKey}`) || '[]'
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) return new Set<string>()
      return new Set<string>(parsed)
    } catch {
      return new Set<string>()
    }
  }

  const applyClientSideFiltersAndSort = async (inputModels: Profile[]): Promise<Profile[]> => {
    let result = [...inputModels]

    // Search
    if (searchTerm && searchTerm.trim().length > 0) {
      const term = normalize(searchTerm)
      result = result.filter(m => normalize(getDisplayName(m)).includes(term))
    }

    const needsOnlineInfo = filterOnline || sortBy === 'online'
    let onlineSet: Set<string> | null = null

    if (needsOnlineInfo) {
      if (!isSupabaseConfigured) {
        onlineSet = new Set(result.filter(m => m.is_online).map(m => m.id))
      } else {
        try {
          const online = await chatAvailabilityService.getOnlineModels()
          onlineSet = new Set((online || []).map(o => o.profile_id))
        } catch (e) {
          logger.error('Error fetching online models for filter', e)
        }
      }
    }

    // Filter online
    if (filterOnline && onlineSet) {
      result = result.filter(m => onlineSet!.has(m.id))
    }

    // Filters by local actions (curtidas/seguidas)
    if (sortBy === 'liked') {
      const likedSet = getLocalIdSet('nlbr_liked_models')
      result = result.filter(m => likedSet.has(m.id))
    }

    if (sortBy === 'followed') {
      const followedSet = getLocalIdSet('nlbr_followed_models')
      result = result.filter(m => followedSet.has(m.id))
    }

    // Sort
    if (sortBy === 'online' && onlineSet) {
      result.sort((a, b) => {
        const aOnline = onlineSet!.has(a.id) ? 0 : 1
        const bOnline = onlineSet!.has(b.id) ? 0 : 1
        if (aOnline !== bOnline) return aOnline - bOnline
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    } else if (sortBy === 'nearby') {
      const state = normalize(nearbyState)
      const city = normalize(nearbyCity)

      if (state || city) {
        const score = (m: Profile) => {
          const mState = normalize(m.state)
          const mCity = normalize(m.city)
          if (state && city && mState === state && mCity === city) return 0
          if (state && mState === state) return 1
          if (city && mCity === city) return 2
          return 3
        }

        result.sort((a, b) => {
          const sa = score(a)
          const sb = score(b)
          if (sa !== sb) return sa - sb
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })
      }
    }

    // Limit after sorting/filtering (client-side)
    if (limit) result = result.slice(0, limit)

    return result
  }

  useEffect(() => {
    fetchModels()
  }, [limit])

  useEffect(() => {
    if (!showTokenOffer) return

    let mounted = true
    const loadOffers = async () => {
      const data = await tokenService.fetchTokenPackages()
      if (!mounted) return

      const activeOffers = data.filter(pkg => pkg.is_active !== false)
      if (activeOffers.length === 0) return

      setTokenOffers(activeOffers)

      const featured = activeOffers.find(pkg => pkg.is_featured)
      const initial = featured?.id || activeOffers[Math.floor(Math.random() * activeOffers.length)]?.id
      setHighlightedOfferId(initial || null)
    }

    loadOffers()

    return () => {
      mounted = false
    }
  }, [showTokenOffer])

  useEffect(() => {
    if (!showTokenOffer || tokenOffers.length === 0) return

    let index = tokenOffers.findIndex(pkg => pkg.id === highlightedOfferId)
    if (index < 0) index = 0

    const interval = setInterval(() => {
      index = (index + 1) % tokenOffers.length
      setHighlightedOfferId(tokenOffers[index].id)
    }, 9000)

    return () => clearInterval(interval)
  }, [showTokenOffer, tokenOffers, highlightedOfferId])

  useEffect(() => {
    if (rawModels.length === 0) return

    const loadRatings = async () => {
      try {
        const ratingsPromises = rawModels.map(async (model) => {
          const stats = await ratingService.getModelRatingStats(model.id)
          return { modelId: model.id, stats }
        })

        const ratingsData = await Promise.all(ratingsPromises)
        const ratingsMap = ratingsData.reduce((acc, { modelId, stats }) => {
          acc[modelId] = { average: stats.average, count: stats.count }
          return acc
        }, {} as Record<string, { average: number; count: number }>)

        setModelRatings(ratingsMap)
      } catch (error) {
        logger.error('Error loading model ratings', error)
      }
    }

    loadRatings()
  }, [rawModels])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const finalModels = await applyClientSideFiltersAndSort(rawModels)
      if (cancelled) return
      setModels(finalModels)
    }

    run()

    return () => {
      cancelled = true
    }
  }, [rawModels, limit, searchTerm, filterOnline, sortBy, nearbyCity, nearbyState])

  const fetchModels = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!isSupabaseConfigured) {
        // Modo demo - usar dados simulados
        await new Promise(resolve => setTimeout(resolve, 500)) // Simular delay
        const baseModels = [...demoModels]
        setRawModels(baseModels)
        logger.warn('Demo mode: Using simulated models data', { count: baseModels.length })
        return
      }

      let query = supabase
        .from('profiles')
        .select('*')
        .eq('is_model', true)           // Apenas modelos
        .eq('approval_status', 'approved')  // Apenas aprovados
        .eq('verified', true)           // Apenas verificados
        .neq('is_blocked', true)        // Excluir usuários bloqueados
        .order('created_at', { ascending: false })

      if (limit) {
        query = query.limit(limit)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError

      const normalizedModels: Profile[] = (data || []).map((m: any) => ({
        ...m,
        name: m?.name || m?.full_name || ''
      }))

      setRawModels(normalizedModels)
      logger.info('Models fetched successfully', { count: data?.length || 0 })
    } catch (err: any) {
      logger.error('Error fetching models', err)
      setError('Erro ao carregar modelos')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {Array.from({ length: limit || 8 }).map((_, index) => (
          <div key={index} className="bg-gray-800 rounded-lg overflow-hidden animate-pulse">
            <div className="aspect-[4/5] bg-gray-700" />
            <div className="p-3 space-y-2">
              <div className="h-3 bg-gray-700 rounded" />
              <div className="h-3 bg-gray-700 rounded w-2/3" />
              <div className="h-2 bg-gray-700 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error}</p>
        <button
          onClick={fetchModels}
          className="mt-4 btn-primary"
        >
          Tentar Novamente
        </button>
      </div>
    )
  }

  if (models.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Nenhuma modelo encontrada</p>
      </div>
    )
  }

  const highlightedOffer = showTokenOffer
    ? tokenOffers.find(pkg => pkg.id === highlightedOfferId)
    : null

  return (
    <div>
      {showTitle && (
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Nossas Modelos
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Conheça as modelos mais incríveis da plataforma. Chat público em tempo real com sistema de tokens.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {showTokenOffer && highlightedOffer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0, rotate: [0, -1.5, 1.5, 0] }}
            transition={{ duration: 2.6, repeat: Infinity, repeatDelay: 1.8 }}
            className="bg-gradient-to-br from-pink-600/20 via-purple-600/20 to-blue-600/20 border border-pink-500/30 rounded-lg overflow-hidden col-span-2 md:col-span-1"
          >
            <Link to="/tokens" className="block h-full">
              <div className="p-6 h-full flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-pink-200 text-sm font-semibold">
                    <Coins className="w-4 h-4" />
                    Oferta de Tokens
                  </div>
                  <span className="text-xs bg-pink-500/20 text-pink-100 px-2 py-1 rounded-full">
                    Novo
                  </span>
                </div>

                <div className="mt-6">
                  <div className="text-white text-lg font-semibold">
                    {highlightedOffer.description || 'Pacote em destaque'}
                  </div>
                  <div className="text-2xl font-bold text-white mt-2">
                    {highlightedOffer.tokens}
                    {highlightedOffer.bonus ? (
                      <span className="text-green-300 text-lg">+{highlightedOffer.bonus}</span>
                    ) : null}
                    <span className="text-sm text-white/70 ml-2">tokens</span>
                  </div>
                  <div className="text-pink-200 text-lg font-semibold mt-1">
                    R$ {highlightedOffer.price.toFixed(2)}
                  </div>
                </div>

                <div className="mt-6">
                  <span className="inline-flex items-center justify-center bg-pink-600 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                    Comprar agora
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        )}
        {models.map((model, index) => (
          <motion.div
            key={model.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors group"
          >
            <Link to={`/profile/${model.id}`} className="block">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={model.profile_image_url || model.photos?.[0] || model.avatar_url || '/default-avatar.svg'}
                  alt={model.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Status Online */}
                <div className="absolute top-3 left-3">
                  <OnlineIndicator profileId={model.id} showLabel={true} size="md" />
                </div>

                {/* Overlay com ações */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-pink-600 text-white p-2 rounded-full"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-gray-700 text-white p-2 rounded-full"
                    >
                      <Eye className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </div>

              <div className="p-2 sm:p-3">
                <h3 className="text-sm sm:text-base font-semibold text-white mb-1 group-hover:text-pink-400 transition-colors truncate">
                  {model.name}
                </h3>
                
                {model.bio && (
                  <p className="hidden sm:block text-gray-400 text-xs mb-2 overflow-hidden" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {model.bio}
                  </p>
                )}

                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>
                      {model.last_seen 
                        ? `Visto ${new Date(model.last_seen).toLocaleDateString()}`
                        : 'Novo'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Star className="w-3 h-3 text-yellow-400" />
                    <span>{modelRatings[model.id]?.average.toFixed(1) || '0.0'}</span>
                  </div>
                </div>

                {/* Fotos count */}
                {model.photos && model.photos.length > 0 && (
                  <div className="mt-2 hidden sm:block text-[11px] text-pink-400">
                    {model.photos.length} foto{model.photos.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {limit && models.length >= limit && (
        <div className="text-center mt-8">
          <Link
            to="/models"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <span>Ver Todas as Modelos</span>
            <Eye className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  )
}

